import {
  CloseOutlined,
  DownOutlined,
  GlobalOutlined,
  UpOutlined
} from '@ant-design/icons';
import { Button, Checkbox, Divider, Dropdown } from 'antd';
// eslint-disable-next-line no-use-before-define
import * as React from 'react';

import Avatar from '../Avatar';
import ProjectUserPicker, {
  useProjectUserDropdownHeight
} from './ProjectUserPicker';
import { ProjectSearchUser } from './assignees';
import styles from './filters.module.css';
import {
  PROJECT_SEARCH_PUBLIC_FEEDBACK_CREATED_BY,
  ProjectSearchFilters
} from './tokens';

export type ProjectSearchCreatedByFilterProps = {
  currentUser: ProjectSearchUser | null;
  filters: ProjectSearchFilters;
  getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
  isContainerOpen: boolean;
  onChange: (filters: ProjectSearchFilters) => void;
  onUserSearchQueryChange?: (query: string) => void;
  showPublicFeedback?: boolean;
  users: ProjectSearchUser[];
};

const toggleToken = (tokens: string[], token: string) =>
  tokens.includes(token)
    ? tokens.filter(value => value !== token)
    : [...tokens, token];

const MAX_DROPDOWN_HEIGHT = 360;
const FIXED_CONTENT_HEIGHT = 176;
const MAX_LIST_HEIGHT = 240;

const CreatedByFilter: React.FC<ProjectSearchCreatedByFilterProps> = ({
  currentUser,
  filters,
  getPopupContainer,
  isContainerOpen,
  onChange,
  onUserSearchQueryChange,
  showPublicFeedback = false,
  users
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const { dropdownHeight, dropdownPanelRef } = useProjectUserDropdownHeight(
    isOpen,
    MAX_DROPDOWN_HEIGHT
  );
  const filterCount = filters.createdBy.length;
  const label = filterCount ? `Created by (${filterCount})` : 'Created by';
  const currentUserId = currentUser ? `${currentUser.id}` : null;
  const pickerUsers = currentUser
    ? users.filter(user => user.id !== currentUser.id)
    : users;
  const listHeight = Math.min(
    MAX_LIST_HEIGHT,
    Math.max(0, dropdownHeight - FIXED_CONTENT_HEIGHT)
  );

  React.useEffect(() => {
    if (!isContainerOpen) setIsOpen(false);
  }, [isContainerOpen]);

  const overlay = (
    <div
      className={styles.compactDropdownPanel}
      ref={dropdownPanelRef}
      style={{ maxHeight: dropdownHeight }}
    >
      <ProjectUserPicker
        compact
        currentUser={currentUser}
        listHeight={listHeight}
        onToggle={userId =>
          onChange({
            ...filters,
            createdBy: toggleToken(filters.createdBy, userId)
          })
        }
        onSearchQueryChange={onUserSearchQueryChange}
        rowHeight={40}
        selectedUserIds={filters.createdBy}
        users={pickerUsers}
      >
        {showPublicFeedback && currentUser?.role !== 'guest' && (
          <>
            <label className={`${styles.assigneeRow} ${styles.compactUserRow}`}>
              <Checkbox
                aria-label="Filter by public feedback"
                checked={filters.createdBy.includes(
                  PROJECT_SEARCH_PUBLIC_FEEDBACK_CREATED_BY
                )}
                onChange={() =>
                  onChange({
                    ...filters,
                    createdBy: toggleToken(
                      filters.createdBy,
                      PROJECT_SEARCH_PUBLIC_FEEDBACK_CREATED_BY
                    )
                  })
                }
              />
              <span className={styles.publicFeedbackIcon}>
                <GlobalOutlined />
              </span>
              <span className={styles.publicFeedbackText}>
                <span
                  className={`${styles.assigneeName} ${styles.compactUserName}`}
                >
                  Public feedback
                </span>
                <span className={styles.publicFeedbackHint}>
                  Anonymous submissions
                </span>
              </span>
            </label>
            <Divider className={`${styles.divider} ${styles.compactDivider}`} />
          </>
        )}
        {currentUser && currentUserId && (
          <>
            <label className={`${styles.assigneeRow} ${styles.compactUserRow}`}>
              <Checkbox
                checked={filters.createdBy.includes(currentUserId)}
                onChange={() =>
                  onChange({
                    ...filters,
                    createdBy: toggleToken(filters.createdBy, currentUserId)
                  })
                }
              />
              <Avatar
                member={currentUser}
                size="small"
                className={styles.avatar}
              />
              <span
                className={`${styles.assigneeName} ${styles.compactUserName}`}
              >
                Me
              </span>
            </label>
            <Divider className={`${styles.divider} ${styles.compactDivider}`} />
          </>
        )}
      </ProjectUserPicker>
    </div>
  );

  return (
    <Dropdown
      getPopupContainer={
        getPopupContainer || (trigger => trigger.parentElement || document.body)
      }
      onOpenChange={setIsOpen}
      open={isOpen}
      overlay={overlay}
      overlayClassName={styles.compactDropdownOverlay}
      placement="bottomLeft"
      trigger={['click']}
    >
      {filterCount ? (
        <span className={styles.selectedDropdownTrigger}>
          <Button
            aria-label="Edit created by filter"
            className={styles.selectedDropdownLabel}
            shape="round"
            size="small"
            type="primary"
          >
            {label}
          </Button>
          <Button
            aria-label="Clear created by filter"
            className={styles.selectedDropdownClear}
            icon={<CloseOutlined />}
            onClick={event => {
              event.stopPropagation();
              setIsOpen(false);
              onChange({ ...filters, createdBy: [] });
            }}
            shape="circle"
            size="small"
            type="primary"
          />
        </span>
      ) : (
        <Button
          aria-label={label}
          shape="round"
          size="small"
          type={isOpen ? 'primary' : 'default'}
        >
          {label}
          {isOpen ? <UpOutlined /> : <DownOutlined />}
        </Button>
      )}
    </Dropdown>
  );
};

export default CreatedByFilter;
